import { Store } from 'vuex';
import { AxiosInstance, AxiosResponse } from 'axios';
import some from 'lodash/some';

import { PreConfiguredMeal, Menu, MenuItemLookupResult, OrderMeal, MenuItem, TortillaItem, CompressedMenu } from '@/models';
import { MUTATIONS as MENU_MUTATIONS, MenuCacheEntry, RestaurantMenuCacheEntry } from '@/store/menu';
import { AppState } from '@/store';

import { ApiService } from '../api/api.service';
import { OrderService } from '../order/order.service';
import { Logger } from '../logger/logger';
import { isDefined, freezeMenu } from '@/helpers';
import remove from 'lodash/remove';
import { AnalyticsService } from '../analytics/analytics.service';
import { CountryCode } from '@/store/ui';

import * as lz4 from 'lz4js';

type MenuItemLike = Pick<MenuItem, 'unitPrice' | 'unitDeliveryPrice'>;

export interface MenuService {
  getLatestPreConfiguredMeals(restaurantId: number, includeUnavailableItems: boolean): Promise<PreConfiguredMeal[]>;
  refreshPreConfiguredMeals(restaurantId?: number, includeUnavailableItems?: boolean): Promise<PreConfiguredMeal[]>;
  refreshMenuForRestaurant(restaurantId: number, getLatestMenu: boolean): Promise<Menu>;
  refreshUniversalMenu(getLatestMenu: boolean): Promise<Menu>;
  refreshUniversalMenuFull(getLatestMenu: boolean): Promise<Menu>;
  areTortillaItemsAvailable(menuItemType: TortillaItem): boolean;
  isMenuItemTypeAvailable(menuItemType: string): boolean;
  refreshActiveMenu(): Promise<void>;
  cacheMenuForRestaurant(restaurantId: number, etag: string, menu: Menu): void;
  waitForRestaurantMenuLoad(): Promise<void>;
  getCachedMenuForRestaurant(restaurantId: number): { etag: string; menu: Menu } | undefined;
  getItemPrice(item: MenuItemLike): number;
  /** this will need updated if we have a new top level category such as desserts */
  getFirstTopLevelMenuItemId(meal: OrderMeal): string;
  getFirstTopLevelMenuItemIdForPcm(meal: PreConfiguredMeal): string;
  setPreConfiguredMeals(preConfiguredMeals: PreConfiguredMeal[]): void;
}

export class AppMenuService extends ApiService implements MenuService {
  MAX_CACHED_MENU_COUNT = 3;
  constructor(
    private order: OrderService,
    store: Store<AppState>,
    httpClient: AxiosInstance,
    logger: Logger,
    private config: {
      channel: string;
    },
    analytics: AnalyticsService,
  ) {
    super(store, httpClient, logger, analytics);
  }

  setPreConfiguredMeals(preConfiguredMeals: PreConfiguredMeal[]) {
    this.store.commit(MENU_MUTATIONS.SET_PRECONFIGURED_MEALS, preConfiguredMeals);
  }

  async getLatestPreConfiguredMeals(restaurantId: number, includeUnavailableItems: boolean) {
    return await this.httpClient
      .get(`menuinnovation/v1/restaurants/${restaurantId}/onlinemeals?includeUnavailableItems=${includeUnavailableItems}`)
      .then((res: any) => res.data);
  }

  async getUniversalPreConfiguredMeals() {
    const mappedCountry = this.getMappedCountryCode();
    const country = mappedCountry || this.store.state.ui.countryProfile.countryCode;
    return await this.httpClient.get(`menuinnovation/v1/universalmeals/online?country=${country}`).then((res: any) => res.data);
  }

  async refreshPreConfiguredMeals(restaurantId: number, includeUnavailableItems: boolean = false) {
    const preConfiguredMeals = restaurantId ? await this.getLatestPreConfiguredMeals(restaurantId, includeUnavailableItems) : await this.getUniversalPreConfiguredMeals(); // universal PCEs temporaritly turned off
    this.setPreConfiguredMeals(preConfiguredMeals);

    return preConfiguredMeals;
  }

  refreshMenuForRestaurant(restaurantId: number, forceLatest: boolean) {
    return this.getMenuForRestaurant(restaurantId, forceLatest, true, true);
  }

  async getMenuForRestaurant(restaurantId: number, forceLatest: boolean, cache: boolean, includeUnavailableItems: boolean): Promise<Menu> {
    const selectedRestaurant = this.store.state.restaurant.selectedRestaurant;
    const activeMenu = this.store.state.menu.activeMenu;

    // If we're trying to get the menu for the active restaurant, we've already got that so bail out early
    if (selectedRestaurant?.restaurantNumber === restaurantId && activeMenu?.restaurantId === selectedRestaurant.restaurantNumber) {
      return activeMenu;
    }

    const cachedMenu = this.getCachedMenuForRestaurant(restaurantId);

    this.store.commit(MENU_MUTATIONS.IS_LOADING_RESTAURANT_MENU, true);

    return this.httpClient
    .get(`menuinnovation/v1/restaurants/${restaurantId}/onlinemenu/compressed?channelId=${this.config.channel}&includeUnavailableItems=${includeUnavailableItems}`, {
        headers: {
          'If-None-Match': forceLatest ? '0' : cachedMenu?.etag,
        },
        validateStatus: status => [200, 304].includes(status),
      })
      .then((r: AxiosResponse<CompressedMenu>) => {
        if (r.status == 304 && cachedMenu) {
          return cachedMenu.menu;
        }
   
        const compressedMenuWrapper = r.data;
        const compressedMenu = Uint8Array.from(atob(compressedMenuWrapper.compressedMenu), c => c.charCodeAt(0));
        const destination = new Uint8Array(compressedMenuWrapper.decompressedMenuSize);

        lz4.decompressBlock(compressedMenu, destination, 0, compressedMenu.length, 0);

        const enc = new TextDecoder('utf-8');
        const menu = JSON.parse(enc.decode(destination));

        if (cache) {
          this.cacheMenuForRestaurant(restaurantId, r.headers['etag'], menu);
        }

        return menu;
      })
      .catch(e => this.logAndThrowApiError('getMenuForRestaurant', e))
      .finally(() => this.store.commit(MENU_MUTATIONS.IS_LOADING_RESTAURANT_MENU, false));
  }

  waitForRestaurantMenuLoad(): Promise<void> {
    if (
      this.store.state.menu.activeMenu?.restaurantId &&
      this.store.state.restaurant.selectedRestaurant?.restaurantNumber === this.store.state.menu.activeMenu?.restaurantId
    ) {
      return Promise.resolve();
    }

    return new Promise((res, rej) => {
      const menuWatcher = this.store.watch(
        state => state.menu.activeMenu,
        () => {
          if (this.store.state.menu.activeMenu?.restaurantId) {
            res();
            menuWatcher(); // teardown watcher
          }
        },
      );
    });
  }

  refreshUniversalMenu(getLatestMenu: boolean): Promise<Menu> {
    return this.getUniversalMenu(getLatestMenu, true);
  }

  refreshUniversalMenuFull(getLatestMenu: boolean): Promise<Menu> {
    return this.getUniversalMenuFull(getLatestMenu, true);
  }

  areTortillaItemsAvailable(menuItemType: TortillaItem): boolean {
    const activeMenu = this.store.state.menu.activeMenu;

    if (!activeMenu || !activeMenu.entrees) {
      return false;
    }

    return some(activeMenu.entrees, entree => {
      if (entree.itemType == menuItemType && entree.isItemAvailable && entree.contents) {
        return some(entree.contents, item => item.itemType == TortillaItem.Tortillas && item.isItemAvailable);
      }
    });
  }

  isMenuItemTypeAvailable(menuItemType: string): boolean {
    const entrees = this.store.state.menu.activeMenu?.entrees || [];

    return some(entrees, ['itemType', menuItemType]);
  }

  async getUniversalMenu(forceLatest: boolean, cache: boolean): Promise<Menu> {
    const cachedMenu = this.store.state.menu.universalMenu;
    const mappedCountry = this.getMappedCountryCode();
    const country = mappedCountry || this.store.state.ui.countryProfile.countryCode;

    return this.httpClient
      .get(`menuinnovation/v1/universalmenus/online?country=${country}`, {
        headers: {
          'If-None-Match': forceLatest ? '0' : cachedMenu && cachedMenu.etag,
        },
        validateStatus: status => status === 200 || status === 304,
      })
      .then((r: AxiosResponse<Menu>) => {
        if (r.status == 304 && cachedMenu && cachedMenu.menu) {
          return cachedMenu.menu;
        }

        const menu = r.data;
        if (cache) {
          this.cacheUniversalMenu(r.headers['etag'], menu);
        }

        return menu;
      })
      .catch(e => this.logAndThrowApiError('getUniversalMenu', e));
  }

  getCountryUrl(): string {
    const baseUrl = 'menuinnovation/v1/universalmenus/menurules';
    const mappedCountry = this.getMappedCountryCode();
    
    if (mappedCountry) {
      return `${baseUrl}?country=${mappedCountry}`;
    }

    return baseUrl;
  }

  private getMappedCountryCode(): string | undefined {
    const country = this.store.state.ui.countryProfile.countryCode;
    const countryMap: { [key: string]: string } = {
      US: 'US',
      CA: 'CA',
      GB: 'UK',
      FR: 'FR',
      DE: 'DE',
    };

    return countryMap[country];
  }

  async getUniversalMenuFull(forceLatest: boolean, cache: boolean): Promise<Menu> {
    const cachedMenu = this.store.state.menu.universalMenuFull;

    return this.httpClient
      .get(this.getCountryUrl(), {
        headers: {
          'If-None-Match': forceLatest ? '0' : cachedMenu && cachedMenu.etag,
        },
        validateStatus: status => status === 200 || status === 304,
      })
      .then((r: AxiosResponse<Menu>) => {
        if (r.status == 304 && cachedMenu && cachedMenu.menu) {
          return cachedMenu.menu;
        }

        const menu = r.data;
        if (cache) {
          this.cacheUniversalMenuFull(r.headers['etag'], menu);
        }

        return menu;
      })
      .catch(e => this.logAndThrowApiError('getUniversalMenuFull', e));
  }

  async refreshActiveMenu() {
    const restaurant = this.store.state.restaurant.selectedRestaurant;
    const restaurantNumber = restaurant && restaurant.restaurantNumber;

    const menu = isDefined(restaurantNumber) ? await this.getMenuForRestaurant(restaurantNumber, false, true, true) : await this.getUniversalMenu(false, true);

    this.setActiveMenu(menu);
  }

  getItemPrice(item: MenuItemLike): number {
    const currentOrder = this.store.state.order;
    if(currentOrder?.isGroupOrderParticipant &&
      currentOrder?.pendingGroupOrder?.order.delivery) {
      return item.unitDeliveryPrice;
    }

    return this.order.isDelivery() ? item.unitDeliveryPrice : item.unitPrice;
  }

  getFirstTopLevelMenuItemId(meal: OrderMeal): string {
    if (meal.entrees && meal.entrees.length > 0) {
      return meal.entrees[0].menuItemId;
    } else if (meal.sides && meal.sides.length > 0) {
      return meal.sides[0].menuItemId;
    } else if (meal.drinks && meal.drinks.length > 0) {
      return meal.drinks[0].menuItemId;
    }

    this.logger.unreachable('Can not getFirstTopLevelMenuItemId for a meal with no entree, side or drink.');
    throw Error('meal is in invalid state');
  }

  getFirstTopLevelMenuItemIdForPcm(meal: PreConfiguredMeal): string {
    if (meal.entree && meal.entree.itemId) {
      return meal.entree.itemId;
    }

    this.logger.unreachable('Can not getFirstTopLevelMenuItemId for a preconfigured meal with no entree, side or drink.');
    throw Error('preconfigured meal is in invalid state');
  }

  cacheMenuForRestaurant(restaurantId: number, etag: string, menu: Menu) {
    const entry: RestaurantMenuCacheEntry = { restaurantId, etag, menu: freezeMenu(menu) };

    this.store.commit(MENU_MUTATIONS.CACHE_MENU_FOR_RESTAURANT, entry);

    // maintain the cache index order and remove the least recently used element;
    const cachedMenuRestaurantIds = this.getCachedMenuRestaurantIdsAndResetPosition(restaurantId);

    if (cachedMenuRestaurantIds.length > this.MAX_CACHED_MENU_COUNT) {
      this.store.commit(MENU_MUTATIONS.REMOVE_CACHED_MENU_FOR_RESTAURANT, cachedMenuRestaurantIds.pop());
    }
    this.store.commit(MENU_MUTATIONS.SET_CACHED_MENU_RESTAURANT_IDS, cachedMenuRestaurantIds);
  }

  getCachedMenuForRestaurant(
    restaurantId: number,
  ):
    | {
        etag: string;
        menu: Menu;
      }
    | undefined {
    const menu = this.store.state.menu.cachedMenus[restaurantId];
    if (menu) {
      this.getCachedMenuRestaurantIdsAndResetPosition(restaurantId);
    }

    return menu;
  }

  private getCachedMenuRestaurantIdsAndResetPosition(restaurantId: number): number[] {
    const cachedMenuRestaurantIds = this.store.state.menu.cachedMenuRestaurantIds;
    remove(cachedMenuRestaurantIds, n => n === restaurantId);
    cachedMenuRestaurantIds.unshift(restaurantId);
    return cachedMenuRestaurantIds;
  }

  private createMenuLookupMap(menu: Menu | null): Map<string, MenuItemLookupResult> {
    const lookup = new Map<string, MenuItemLookupResult>();

    if (!menu) {
      return lookup;
    }

    // Add Entrees
    for (const entree of menu.entrees || []) {
      if (!lookup.has(entree.itemId)) {
        lookup.set(entree.itemId, {
          menuItemName: entree.itemName,
          menuItemType: entree.itemType,
          primaryFillingName: entree.primaryFillingName,
          unitPrice: entree.unitPrice,
          unitDeliveryPrice: entree.unitDeliveryPrice,
          isEligibleForDelivery: entree.eligibleForDelivery,
          isItemAvailable: entree.isItemAvailable,
        });
      }

      if (entree.contents) {
        entree.contents.map(item => {
          if (!lookup.has(item.itemId)) {
            lookup.set(item.itemId, {
              menuItemName: item.itemName,
              menuItemType: item.itemType,
              unitPrice: item.unitPrice,
              unitDeliveryPrice: item.unitDeliveryPrice,
              isEligibleForDelivery: item.eligibleForDelivery,
              isItemAvailable: item.isItemAvailable,
            });
          }
        });
      }
    }

    // Add Sides & Drinks
    for (const item of [...(menu.sides || []), ...(menu.drinks || [])]) {
      if (!lookup.has(item.itemId)) {
        lookup.set(item.itemId, {
          menuItemName: item.itemName,
          menuItemType: item.itemType,
          unitPrice: item.unitPrice,
          unitDeliveryPrice: item.unitDeliveryPrice,
          isEligibleForDelivery: item.eligibleForDelivery,
          isItemAvailable: item.isItemAvailable,
        });
      }
    }

    return lookup;
  }

  private setActiveMenu(menu: Menu) {
    this.store.commit(MENU_MUTATIONS.SET_ACTIVE_MENU, freezeMenu(menu));

    const menuItemLookupMap = this.createMenuLookupMap(menu);
    this.store.commit(MENU_MUTATIONS.SET_MENU_ITEM_LOOKUP_MAP, menuItemLookupMap);
  }

  private cacheUniversalMenu(etag: string, menu: Menu) {
    const entry: MenuCacheEntry = { etag, menu: freezeMenu(menu) };

    this.store.commit(MENU_MUTATIONS.CACHE_UNIVERSAL_MENU, entry);
  }

  private cacheUniversalMenuFull(etag: string, menu: Menu) {
    const entry: MenuCacheEntry = { etag, menu: freezeMenu(menu) };

    this.store.commit(MENU_MUTATIONS.CACHE_UNIVERSAL_MENU_FULL, entry);
  }
}
